import {
  DownloadTwoTone,
  ErrorTwoTone,
  FilterAltTwoTone,
  MoreVertTwoTone,
  VisibilityTwoTone,
} from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { guideCustomerVisaListPrint, guideTourCustomerVisaList } from '../api';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { useAuth } from '../hooks/useAuth';
import { download } from '../utils';
import { ICustomerVisaList } from '../utils/types';
import GeneralCustomerListFilterDialog from './GeneralCustomerListFilterDialog';
import PdfViewer from './PdfViewer';

type GuideCustomerVisaListProps = {
  salesIndividualId: string;
  showCustomVisaColumns?: boolean;
  problemPax?: string;
};

export default function GuideTourVisaAndPassportTable({
  salesIndividualId,
  showCustomVisaColumns,
}: GuideCustomerVisaListProps) {
  const [customerVisaList, setCustomerVisaList] = useState<ICustomerVisaList[]>(
    [],
  );
  const { token } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState('');
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedSalesGroups, setSelectedSalesGroups] = useState<string[]>([]);
  const [selectedTourGroups, setSelectedTourGroups] = useState<string[]>([]);
  const [assignmentIds, setAssignmentIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const showSnackbar = useAlertSnackbar();

  const visaNotSavedCount = customerVisaList.filter(
    (row) => row.state !== 'VISA_SAVED',
  ).length;

  const passportNotExistCount = customerVisaList.filter(
    (row) => !row.passportNumber,
  ).length;

  const sortedCustomerVisaList = useMemo(() => {
    if (showCustomVisaColumns) {
      return [...customerVisaList].sort((a, b) => {
        if (a.state === 'VISA_SAVED' && b.state !== 'VISA_SAVED') {
          return -1;
        }
        if (a.state !== 'VISA_SAVED' && b.state === 'VISA_SAVED') {
          return 1;
        }
        return 0;
      });
    } else if (!showCustomVisaColumns) {
      return [...customerVisaList].sort((a, b) => {
        if (a.passportNumber && !b.passportNumber) {
          return 1;
        }
        if (!a.passportNumber && b.passportNumber) {
          return -1;
        }
        return 0;
      });
    }
    return customerVisaList;
  }, [customerVisaList, showCustomVisaColumns]);

  const handlePrint = () => {
    fetchGuideTourAccommodationGroupSettledListPrint(
      salesIndividualId,
      assignmentIds,
    );
  };

  const fetchGuideTourAccommodationGroupSettledListPrint = async (
    salesIndividualId: string,
    assignmentIds: number[],
  ) => {
    try {
      const res = await guideCustomerVisaListPrint(
        salesIndividualId,
        assignmentIds,
        token,
      );
      const objectURL = window.URL.createObjectURL(await res.blob());
      const fileName = ' ' + t('guideGeneralCustomerList.customerList');
      download(objectURL, fileName);
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    }
  };

  const tourGroupOptions = Array.from(
    new Set(
      customerVisaList
        .filter(
          (row) =>
            row?.tourGroupName !== undefined && row?.tourGroupName !== null,
        )
        .map((row) => row.tourGroupName),
    ),
  );

  const salesGroupOptions = Array.from(
    new Set(
      customerVisaList
        .filter(
          (row) =>
            row?.salesGroupName !== undefined && row?.salesGroupName !== null,
        )
        .map((row) => row.salesGroupName),
    ),
  );

  useEffect(() => {
    fetchGuideTourRelatedCustomerList(salesIndividualId);
  }, [salesIndividualId]);

  const fetchGuideTourRelatedCustomerList = async (
    salesIndividualId: string,
  ) => {
    try {
      const res = await guideTourCustomerVisaList(salesIndividualId, token);
      const data = await res.json();
      if (res?.status === 200) {
        setCustomerVisaList(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (assignmentId: number) => {
    setAssignmentIds((prev) =>
      prev.includes(assignmentId)
        ? prev.filter((id) => id !== assignmentId)
        : [...prev, assignmentId],
    );
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setAssignmentIds([]);
    } else {
      setAssignmentIds(
        customerVisaList
          .filter((row) => row.state === 'VISA_SAVED')
          .map((row) => row.assignmentId),
      );
    }
    setSelectAll(!selectAll);
  };

  const [pdfOpen, setPdfOpen] = useState(false); // State to control the PDF viewer dialog
  const [pdfUrl, setPdfUrl] = useState<string>(''); // State to hold the URL of the PDF

  const handleViewVisa = (visaDocFileFullPath: string) => {
    setPdfUrl(visaDocFileFullPath);
    setPdfOpen(true);
  };

  const handleShare = (
    event: React.MouseEvent<HTMLElement>,
    visaDocFileFullPath: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setPdfUrl(visaDocFileFullPath);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(pdfUrl);
    showSnackbar(t('guideCustomerOperationState.copied'), 'success');
    handleCloseMenu();
  };

  const handleShareWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(pdfUrl)}`;
    window.open(whatsappUrl, '_blank');
    handleCloseMenu();
  };

  return (
    <Box>
      {loading && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <CircularProgress color="secondary" />
          <Typography sx={{ fontSize: 18, mt: 2 }}>
            {t('balancesQueryPage.loading')}
          </Typography>
        </Box>
      )}
      {!loading && (
        <>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {showCustomVisaColumns && (
                <IconButton onClick={handlePrint} sx={{ mr: 1 }}>
                  <DownloadTwoTone />
                </IconButton>
              )}

              <IconButton
                onClick={() => setOpenFilter(!openFilter)}
                sx={{ mr: 1 }}
              >
                <FilterAltTwoTone />
              </IconButton>
              <TextField
                label={t('guideTourVisaAndPassportList.searchPerson')}
                placeholder={t('guideTourVisaAndPassportList.searchWithName')}
                variant="outlined"
                color="secondary"
                size="small"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                sx={{
                  width: '100%',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon color="secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <Alert severity="error" sx={{ alignItems: 'center', mb: 1, mt: 1 }}>
            {!showCustomVisaColumns
              ? t('guideTourVisaAndPassportList.passportWaitingPax', {
                  pax: passportNotExistCount,
                })
              : t('guideTourVisaAndPassportList.visaWaitingPax', {
                  pax: visaNotSavedCount,
                })}
          </Alert>
          <GeneralCustomerListFilterDialog
            open={openFilter}
            onClose={() => setOpenFilter(false)}
            tourGroupOptions={tourGroupOptions}
            salesGroupOptions={salesGroupOptions}
            selectedTourGroups={selectedTourGroups}
            setSelectedTourGroups={setSelectedTourGroups}
            selectedSalesGroups={selectedSalesGroups}
            setSelectedSalesGroups={setSelectedSalesGroups}
          />
          <TableContainer component={Paper} sx={{ mt: 1, border: 'none' }}>
            <Table size="small" aria-label="visa table">
              <TableHead>
                <TableRow>
                  {showCustomVisaColumns && (
                    <TableCell
                      sx={{
                        border: (t) => '1px solid ' + t.palette.divider,
                        width: '10px ',
                        textAlign: 'center',
                      }}
                      align="center"
                    >
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                        size="small"
                      />
                      {t('guideTourVisaAndPassportList.selectAll')}
                    </TableCell>
                  )}

                  <TableCell
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideTourVisaAndPassportList.customerName')}
                  </TableCell>

                  {!showCustomVisaColumns && (
                    <TableCell
                      align="center"
                      sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                    >
                      {t('guideTourVisaAndPassportList.passportNo')}
                    </TableCell>
                  )}

                  {showCustomVisaColumns && (
                    <TableCell
                      sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                      align="center"
                    >
                      {t('guideTourVisaAndPassportList.visaState')}
                    </TableCell>
                  )}

                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideTourVisaAndPassportList.salesOffice')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideTourVisaAndPassportList.tourGroup')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCustomerVisaList
                  .filter(
                    (row) =>
                      row.customerFullName
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) &&
                      (selectedTourGroups.length === 0 ||
                        selectedTourGroups.includes(row.tourGroupName)) &&
                      (selectedSalesGroups.length === 0 ||
                        selectedSalesGroups.includes(row.salesGroupName)),
                  )
                  .map((row) => (
                    <TableRow key={row.customerFullName}>
                      {showCustomVisaColumns && (
                        <TableCell
                          sx={{
                            border: (t) => '1px solid ' + t.palette.divider,
                          }}
                          component="th"
                          scope="row"
                          align="center"
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              checked={assignmentIds.includes(row.assignmentId)}
                              onChange={() =>
                                handleCheckboxChange(row.assignmentId)
                              }
                              disabled={row.state !== 'VISA_SAVED'}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                      )}

                      <TableCell
                        sx={{
                          border: (t) => '1px solid ' + t.palette.divider,
                        }}
                        component="th"
                        scope="row"
                        align="left"
                      >
                        {row.customerFullName}
                      </TableCell>

                      {!showCustomVisaColumns && (
                        <TableCell
                          sx={{
                            border: (t) => '1px solid ' + t.palette.divider,
                            textAlign: 'center',
                            justifyItems: 'center',
                          }}
                          component="th"
                          scope="row"
                          align="center"
                        >
                          {row.passportNumber ? (
                            row.passportNumber
                          ) : (
                            <Stack direction="row" gap={1} alignItems="center">
                              <ErrorTwoTone fontSize="small" color="error" />
                              {t(
                                'guideTourVisaAndPassportList.waitingForPassport',
                              )}
                            </Stack>
                          )}
                        </TableCell>
                      )}

                      {showCustomVisaColumns && (
                        <TableCell
                          sx={{
                            border: (t) => '1px solid ' + t.palette.divider,
                            textAlign: 'center',
                            verticalAlign: 'middle',
                          }}
                          component="th"
                          scope="row"
                          align="center"
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 4,
                            }}
                          >
                            {row.state === 'VISA_SAVED' ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                }}
                              >
                                {t('guideTourVisaAndPassportList.visaSaved')}
                                <IconButton
                                  onClick={(e) =>
                                    handleShare(e, row.visaDocFileFullPath)
                                  }
                                  disabled={row.state !== 'VISA_SAVED'}
                                  size="small"
                                >
                                  <MoreVertTwoTone fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Stack
                                direction="row"
                                gap={1}
                                alignItems="center"
                                justifyContent="center"
                              >
                                <ErrorTwoTone fontSize="small" color="error" />
                                {t(
                                  'guideTourVisaAndPassportList.waitingForVisa',
                                )}
                              </Stack>
                            )}
                          </Box>
                        </TableCell>
                      )}

                      <TableCell
                        sx={{
                          border: (t) => '1px solid ' + t.palette.divider,
                        }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.salesGroupName || '-'}
                        {row.salesOfficeName !== row.salesGroupName
                          ? '(' + row.salesOfficeName + ')'
                          : ''}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: (t) => '1px solid ' + t.palette.divider,
                        }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.tourGroupName || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handleViewVisa(pdfUrl)}>
          <VisibilityTwoTone color="primary" />
          <Typography sx={{ ml: 1 }}>
            {t('guideTourVisaAndPassportList.viewVisa')}
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleCopyLink}>
          <ContentCopyIcon color="primary" />
          <Typography sx={{ ml: 1 }}>
            {t('guideTourVisaAndPassportList.copyLink')}
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleShareWhatsApp}>
          <WhatsAppIcon color="primary" />
          <Typography sx={{ ml: 1 }}>
            {t('guideTourVisaAndPassportList.shareInWhatsApp')}
          </Typography>
        </MenuItem>{' '}
      </Menu>
      <PdfViewer
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        pdfUrl={pdfUrl}
      />
    </Box>
  );
}
